import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageCircle, Minimize2, Send, Sparkles, X } from "lucide-react";
import { AdminAiChat, PublicAiChat } from "@/lib/api";
import { useRecaptchaToken } from "@/lib/recaptcha";

const STORAGE_KEY_PREFIX = "sf-ai-chat-session-";

/**
 * Reusable AI chat panel. `variant="admin"` sends to the admin route with a
 * model-selector; `variant="public"` sends to the public route with reCAPTCHA
 * (soft check on the backend). The component persists its session_id in
 * localStorage per variant so a reload keeps the conversation going.
 */
export function AiChatPanel({ variant = "admin", onClose }) {
  const isAdmin = variant === "admin";
  const executeRecaptcha = useRecaptchaToken();

  const [sessionId, setSessionId] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY_PREFIX + variant) : null,
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [notice, setNotice] = useState(null);
  const listRef = useRef(null);

  // Load available models (admin) + hydrate any prior transcript.
  useEffect(() => {
    (isAdmin ? AdminAiChat : PublicAiChat).models()
      .then((r) => {
        setModels(r.models || []);
        setSelectedModel(r.default || "");
      })
      .catch(() => {});
    if (sessionId) {
      const api = isAdmin ? AdminAiChat : PublicAiChat;
      api.history(sessionId).then((r) => setMessages(r.messages || [])).catch(() => {});
    } else {
      // Seed a friendly first message.
      setMessages([
        {
          role: "assistant",
          content: isAdmin
            ? "Hi! I'm your Sahyog admin helper. Ask me to summarise a lead, draft a customer reply, or explain a credit-report field."
            : "Hi there! I'm the Sahyog Finance assistant. Ask me about our loan products, eligibility, EMIs, or how to apply.",
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to the latest message.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    setSending(true);
    // Optimistic user echo.
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      let reply;
      if (isAdmin) {
        reply = await AdminAiChat.send({
          session_id: sessionId,
          message: text,
          model: selectedModel,
        });
      } else {
        const token = (await executeRecaptcha("ai-chat")) || "";
        reply = await PublicAiChat.send({
          session_id: sessionId,
          message: text,
          recaptcha_token: token,
          model: selectedModel || undefined,
        });
      }
      if (!sessionId) {
        setSessionId(reply.session_id);
        try {
          localStorage.setItem(STORAGE_KEY_PREFIX + variant, reply.session_id);
        } catch { /* storage may be blocked (private mode) — session simply won't persist */ }
      }
      setNotice(reply.fallback_notice || null);
      setMessages((m) => [...m, { role: "assistant", content: reply.reply, model: reply.model }]);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "AI request failed.");
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, isAdmin, selectedModel, executeRecaptcha, variant]);

  const resetSession = () => {
    try {
      localStorage.removeItem(STORAGE_KEY_PREFIX + variant);
    } catch {}
    setSessionId(null);
    setMessages([
      {
        role: "assistant",
        content: isAdmin
          ? "Fresh session started."
          : "Hi again! Ask me anything about our loan products.",
      },
    ]);
  };

  return (
    <div
      data-testid={`ai-chat-${variant}`}
      className="flex flex-col h-full bg-[#0d1428] text-white border border-[#D4AF37]/25 rounded-2xl overflow-hidden"
    >
      {/* Header */}
      <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b border-white/10 bg-black/30 ${isAdmin ? "" : "pr-20"}`}>
        <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
          <Sparkles size={14} className="text-[#F0D788]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {isAdmin ? "Sahyog AI Helper" : "Sahyog · Chat with us"}
          </div>
          <div className="text-[10px] text-white/50">
            {isAdmin
              ? "Internal · not audit-authoritative"
              : "Powered by ChatGPT & Grok · replies indicative only"}
          </div>
        </div>
        {models.length > 0 && (
          <select
            data-testid="ai-chat-model-select"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            title={models.find((m) => m.id === selectedModel)?.note || ""}
            className={`text-[10px] bg-black/40 border border-white/15 rounded-md px-1.5 py-1 text-white/85 ${isAdmin ? "max-w-[190px]" : "basis-full order-last"}`}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}{m.available ? "" : " · key missing"}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={resetSession}
          className="text-[10px] text-white/50 hover:text-white/90 border border-white/15 rounded-md px-2 py-1"
          title="Reset conversation"
        >
          Reset
        </button>
        {onClose && (
          <button
            data-testid="ai-chat-close"
            onClick={onClose}
            className="text-white/50 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        data-testid="ai-chat-messages"
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0d1428]"
      >
        {messages.map((m, i) => (
          <div
            key={m.id || `msg-${i}-${m.role}-${(m.content || "").slice(0, 16)}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#FBEAAD]"
                  : "bg-white/[0.05] border border-white/10 text-white/90"
              }`}
            >
              {m.content}
              {isAdmin && m.model && (
                <div className="text-[9px] uppercase tracking-wider text-white/40 mt-1">
                  {m.model}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-3.5 py-2 bg-white/[0.05] border border-white/10 text-[12px] text-white/60 inline-flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> thinking…
            </div>
          </div>
        )}
      </div>

      {notice && (
        <div data-testid="ai-chat-fallback-notice" className="mx-4 mb-2 text-[11px] text-amber-100 bg-amber-500/10 border border-amber-500/25 rounded-md px-2.5 py-1.5">
          {notice}
        </div>
      )}
      {error && (
        <div className="mx-4 mb-2 text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/25 rounded-md px-2.5 py-1.5">
          {error}
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-white/10 px-3 py-3 bg-black/40">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder={
              isAdmin
                ? "Summarise the last 5 leads for me…"
                : "What's my EMI for a ₹5 Lakh personal loan for 3 years?"
            }
            className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#D4AF37]/50"
          />
          <button
            data-testid="ai-chat-send"
            onClick={send}
            disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#D4AF37]/20 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#F0D788] text-xs font-semibold disabled:opacity-40"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send
          </button>
        </div>
        <div className="text-[9px] text-white/35 mt-1.5">
          Press Enter to send · Shift+Enter for a new line
        </div>
      </div>
    </div>
  );
}

/**
 * Floating "Chat with us" widget for the public marketing site.
 * Renders a chip in the bottom-right that pops up the full chat panel.
 */
export function PublicChatWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);

  return (
    <>
      {!open && (
        <button
          data-testid="public-chat-launcher"
          onClick={() => {
            setOpen(true);
            setMinimized(false);
          }}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full shadow-lg shadow-black/40 bg-gradient-to-r from-[#D4AF37] to-[#F0D788] text-[#0A1F44] font-semibold text-sm hover:scale-105 transition-transform"
        >
          <MessageCircle size={16} />
          Chat with us
        </button>
      )}
      {open && (
        <div
          className={`fixed z-50 shadow-2xl shadow-black/50 transition-all ${
            minimized
              ? "bottom-5 right-5 w-64 h-14"
              : "bottom-5 right-5 w-[380px] h-[560px] max-h-[85vh]"
          }`}
        >
          {minimized ? (
            <button
              onClick={() => setMinimized(false)}
              className="w-full h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0D788] text-[#0A1F44] font-semibold text-sm flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> Continue chat
            </button>
          ) : (
            <div className="w-full h-full relative">
              <div className="absolute top-2 right-11 z-10">
                <button
                  onClick={() => setMinimized(true)}
                  className="text-white/50 hover:text-white p-1"
                  aria-label="Minimize"
                >
                  <Minimize2 size={14} />
                </button>
              </div>
              <AiChatPanel variant="public" onClose={() => setOpen(false)} />
            </div>
          )}
        </div>
      )}
    </>
  );
}
